import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { makeStyles } from '@mui/styles'
import PerfectScrollbar from 'react-perfect-scrollbar'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import MultiSelectService from '@/components/multi-select/multiSelectService'
import MultiSelectList from '@/components/multi-select/layout/multi-select-list'
import { MultiSelectItemObj } from '@/components/multi-select/types'
import { MultiSelectTypes } from '@/components/multi-select/constants'
import MultiSelectActiveItem from '@/components/multi-select/layout/multi-select-active-item'
import PopperMenu from '@/modules/base/components/popper-menu/popper-menu'
import MultiSelectInput from '@/components/multi-select/layout/multi-select-input'
import NewItemForm from '@/components/multi-select/form/new-item-form'
import { getGroups } from '@/components/multi-select/helpers/getGroups'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { useAppSelector } from '@/redux/store'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  popper: {
    width: 438,
    border: `1px solid ${theme.borderColors.outline}`,
    '&[data-popper-placement*="bottom"]': {
      borderTop: 0,
      borderRadius: '0px 0px 4px 4px',
    },
    '&[data-popper-placement*="top"]': {
      borderBottom: 0,
      borderRadius: '4px 4px 0px 0px',
      '& > .MuiPaper-root': {
        boxShadow: 'none',
      },
    },
  },
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  loader: {
    color: theme.borderColors.outline,
  },
  popperContent: {
    maxHeight: 400,
  },
  scrollbar: {
    maxHeight: 400,
  },
}))

type MultiSelectProps = {
  value?: MultiSelectItemObj,
  valueKey: string,
  hiddenValues?: MultiSelectItemObj[],
  popperScrollElId?: string,
  tenantCollaborationId?: string,
  dataQsId: string,
  onChange?: (item: MultiSelectItemObj) => void,
}

const MultiSelect = (props: MultiSelectProps) => {
  const classes = useStyles()
  const popoverAnchor = useRef()
  const {
    value,
    hiddenValues,
    valueKey,
    popperScrollElId,
    tenantCollaborationId,
    dataQsId,
    onChange,
  } = props
  const userInfo = useAppSelector(state => state.user.info)
  const subscriptions = useAppSelector(state => state.tenant.info.subscriptions)
  const [selectedItem, setSelectedItem] = useState<MultiSelectItemObj>()
  const [popperOpen, setPopperOpen] = useState<boolean>(false)
  const [itemToAdd, setItemToAdd] = useState<{ name: string, multiSelectType: MultiSelectTypes }>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [activeSelectType, setActiveSelectType] = useState<MultiSelectTypes>(MultiSelectTypes.All)
  const [data, setData] = useState({
    customer: [],
    supplier: [],
    user: [],
    client: [],
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const isTaxConsultant = useMemo(() => { return subscriptions?.some(s => s.isTaxConsultant) }, [subscriptions])

  const groups = useMemo(() => {
    return getGroups(userInfo, isTaxConsultant)
  }, [])

  useEffect(() => {
    if (value?.[valueKey] && value?.[valueKey] !== selectedItem?.[valueKey]) {
      setSelectedItem(value)
      let activeType = value.multiSelectType
      switch (activeType) {
        case MultiSelectTypes.CustomerContact:
          activeType = MultiSelectTypes.Customer
          break
        case MultiSelectTypes.SupplierContact:
          activeType = MultiSelectTypes.Supplier
          break
      }

      setActiveSelectType(activeType)
    } else {
      setActiveSelectType(MultiSelectTypes.All)
    }

    return () => {
      setSearchTerm('')
      setActiveSelectType(MultiSelectTypes.All)
    }
  }, [selectedItem, value])

  useEffect(() => {
    if (!popperOpen) { return }

    setIsLoading(true)
    MultiSelectService.getData({
      activeType: activeSelectType,
      offset: 0,
      limit: 100,
      searchTerm,
    }, userInfo, isTaxConsultant, tenantCollaborationId)
      .then((data) => {
        const { customers, suppliers, users, clients } = data
        setData({
          customer: customers,
          supplier: suppliers,
          user: users,
          client: clients,
        })
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.multiSelect.error.default }))
      .finally(() => setIsLoading(false))
  }, [popperOpen, searchTerm])

  const onTypeChange = (option: { type: MultiSelectTypes }): void => {
    setActiveSelectType(option.type)
  }
  const onItemSelected = (item: MultiSelectItemObj): void => {
    // need to add email if selected item has none
    if (!item.email) {
      setPopperOpen(false)
      return setItemToAdd({ ...item, name: item.name })
    }

    setSelectedItem(item)
    onPopoverClose()
    onChange?.(item)
  }
  const onSearchTermChange = (searchTerm: string): void => {
    setSearchTerm(searchTerm)
  }
  const onReset = (): void => {
    setSelectedItem(null)
    setActiveSelectType(MultiSelectTypes.All)
    onChange?.(null)
  }
  const onEdit = (item: MultiSelectItemObj): void => {
    setItemToAdd({ ...item })
  }
  const onPopoverClose = (): void => {
    setPopperOpen(false)
  }
  const onPopoverOpen = (): void => {
    setPopperOpen(true)
  }
  const onAddNewClick = (): void => {
    setItemToAdd({ name: searchTerm, multiSelectType: MultiSelectTypes.External })
    setPopperOpen(false)
  }
  const onAddAbort = (): void => {
    setItemToAdd(null)
    setSearchTerm('')
  }
  const onSaveNew = (itemToAdd: MultiSelectItemObj): void => {
    onItemSelected(itemToAdd)
    setItemToAdd(null)
  }

  const activeItem = useMemo(() => {
    if (!selectedItem) { return null }

    return (
      <MultiSelectActiveItem
        selectedItem={selectedItem}
        onReset={onReset}
        onEdit={() => onEdit(selectedItem)} />
    )
  }, [selectedItem])

  const selectList = useMemo(() => {
    if (!popperOpen || isLoading) { return }

    const items = activeSelectType === MultiSelectTypes.All
      ? [...data.user, ...data.client, ...data.customer, ...data.supplier]
      : data[activeSelectType]

    const filteredItems = items.filter((item) => {
      return hiddenValues?.length
        ? !hiddenValues.find((selectedValue) => selectedValue[valueKey] === item[valueKey])
        : true
    })

    return (
      <MultiSelectList
        items={filteredItems}
        searchTerm={searchTerm}
        selectedItem={selectedItem}
        hiddenValues={hiddenValues}
        onAdd={onAddNewClick}
        onChange={(user) => onItemSelected(user)} />
    )
  }, [popperOpen, isLoading, selectedItem, hiddenValues, activeSelectType])

  return (
    <>
      <div ref={popoverAnchor} data-qs-id={dataQsId}>
        {!activeItem && !itemToAdd && (
          <MultiSelectInput
            popperOpen={popperOpen}
            groups={groups}
            activeSelectType={activeSelectType}
            placeholder={lang.multiSelect.placeholder}
            onOpenPopper={onPopoverOpen}
            onClosePopper={onPopoverClose}
            onSearchTermChange={onSearchTermChange}
            onTypeChange={onTypeChange} />
        )}
        {(activeItem && !itemToAdd) && activeItem}
        {itemToAdd && (
          <NewItemForm
            item={itemToAdd}
            onSave={onSaveNew}
            onAbort={onAddAbort} />
        )}
      </div>
      <PopperMenu
        open={popperOpen}
        placement="bottom"
        arrow={false}
        popperClass={classes.popper}
        scrollElId={popperScrollElId}
        style={{ popperContent: classes.popperContent }}
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 0] },
          },
        ]}
        elementRef={popoverAnchor.current}>
        {isLoading
          ? (
            <div className={classes.loaderWrapper}>
              <CircularProgress className={classes.loader} size={50} variant='indeterminate' />
            </div>
          )
          : (
            <PerfectScrollbar className={classes.scrollbar}>
              {selectList}
            </PerfectScrollbar>
          )}
      </PopperMenu>
    </>
  )
}

export default MultiSelect
